import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import type { ToggleLayerOptions } from 'react-laag';
import { ToggleLayer } from 'react-laag';
import type { TextInputProps } from './TextInput';
import type { CommonInputProps } from './CommonInputProps';
import type { ArrayIfMultiElseNullable, StringRecordSubset } from '../../lib';
import { intl, intlFmt } from '../../lib';
import type { ComboboxDropdownMenuProps } from './internal';
import {
  ComboboxDropdownMenu,
  ComboboxInputElement,
  DEFAULT_COMBOBOX_LAYER_PLACEMENT
} from './internal';
import type { ClassNameProp } from '../common-types';

/**
 * Props for a {@link Select} component.
 *
 * The `SafeItem` gymnastics ensure that the given `itemValueProp` and `itemLabelProp` are string properties of `Item`.
 * Please do not override the SafeItem type parameter.
 *
 * The `Variant` gymnastics are necessary to distinguish between single and multi select safely, in order to correctly type value and onChange with array types or nullable types, respectively.
 *
 */
export interface SelectProps<
  Item,
  Variant extends 'single' | 'multi',
  SafeItem = StringRecordSubset<Item>
> extends Pick<
      CommonInputProps<ArrayIfMultiElseNullable<Variant, Item>>,
      | 'label'
      | 'error'
      | 'onBlur'
      | 'required'
      | 'disabled'
      | 'labelOnTop'
      | 'readOnly'
    >,
    Pick<TextInputProps, 'placeholder' | 'condensed'>,
    Partial<
      Pick<ComboboxDropdownMenuProps<SafeItem>, 'renderItem' | 'hideClearIcon'>
    >,
    ClassNameProp {
  /**
   * The variant of select that should be used.
   */
  variant: Variant;
  /**
   * The selectable items.
   */
  items: SafeItem[];
  /**
   * The property of `Item` to use to distinguish items.
   */
  itemValueProp: keyof SafeItem;
  /**
   * The property of `Item` to use as human-readable label.
   */
  itemLabelProp: keyof SafeItem;
  /**
   * @see {@link CommonInputProps.value}
   */
  value: ArrayIfMultiElseNullable<Variant, SafeItem>;
  /**
   * Callback handler for when the selectedItems changes.
   * @param selectedItems the selected items
   */
  onChange: (selectedItems: ArrayIfMultiElseNullable<Variant, Item>) => void;
  /**
   * Optional CSS classes to add to the downshift menu.
   */
  menuClassName?: string;
  /**
   * On true, onBlur will be triggered when the value changes. Only for "single" variant.
   *
   * One of the use-cases is when the form is validated with onBlur:
   *    since the common behavior of combobox is to call only onChange with item select or deselect,
   *    the error in combobox does not disappear or appear right away when the value is invalid or valid respectively.
   *    This property solves the issue.
   */
  triggerBlurOnChange?: boolean;
  /**
   * Options to configure the ToggleLayer component
   */
  menuOptions?: Partial<Omit<ToggleLayerOptions, 'ResizeObserver'>>;

  /**
   * Max number of shown selected items. When a user selects more, additionally to selected items, there will be shown
   * just a number indicating how many more items are selected.
   */
  tagLimit?: number;

  /**
   * Flag to automatically focus the input element once it gets rendered.
   */
  autoFocus?: boolean;
}

/**
 * A combobox select component.
 * Depending on the props this can be either a single or a multi select.
 *
 * `Variant` is an internal type param needed to relate the types of the variant, value and onChange props.
 *
 * **IMPORTANT**: When using this component, you need to make sure that the closest scroll parent where you want to contain this component is set to
 * `position: relative` (or `absolute` or `fixed`). Otherwise, the dropdown element won't be positioned correctly.
 * For more information see https://v1.react-laag.com/docs/togglelayer/.
 * Alternatively, if setting `position: relative` (or `absolute` or `fixed`) on the closest scroll parent does not work,
 * you can try setting the menu to `fixed` mode but that may not work in all situations.
 * ```tsx
 * <Select
 *   menuOptions={{ fixed: true }}
 * ```
 *
 * TODO: allow deselecting if multi == false
 * TODO: disabled items?, fixed items?
 *
 * @typeParam T the type of the selectable items.
 * @param properties a {@link SelectProps} object
 * @return the created {@link JSX.Element}.
 * @constructor
 */
// overload for single-select (value type)
// eslint-disable-next-line @typescript-eslint/ban-types
export function Select<T extends object>(
  properties: SelectProps<T, 'single'> & {
    variant: 'single';
    value: StringRecordSubset<T> | null;
    onChange: (selectedItems: T | null) => void;
  }
): JSX.Element;

// overload for multi-select (array type)
// eslint-disable-next-line @typescript-eslint/ban-types
export function Select<T extends object>(
  properties: SelectProps<T, 'multi'> & {
    variant: 'multi';
    value: StringRecordSubset<T>[];
    onChange: (selectedItems: T[]) => void;
  }
): JSX.Element;

// eslint-disable-next-line @typescript-eslint/ban-types
export function Select<T extends object, Variant extends 'single' | 'multi'>({
  itemLabelProp,
  itemValueProp,
  className = '',
  condensed = false,
  menuClassName = '',
  triggerBlurOnChange = false,
  menuOptions = {},
  renderItem = (item: StringRecordSubset<T>) => item[itemLabelProp],
  hideClearIcon,
  value,
  onChange,
  onBlur,
  readOnly,
  disabled,
  items,
  variant,
  autoFocus,
  ...props
}: Readonly<SelectProps<T, Variant>>): JSX.Element {
  // type of individual items
  type ItemType = StringRecordSubset<T>;
  // local state of the text entered into the input field for filtering
  const [inputValue, setInputValue] = useState('');
  const itemToString = useCallback(item => item?.[itemLabelProp] ?? '', [
    itemLabelProp
  ]);

  const getSelectedItemsForVariant = useCallback(
    (theVariant: Variant, valueToConvert: typeof value) => {
      if (theVariant === 'single' && valueToConvert !== null) {
        return [valueToConvert] as ItemType[];
      } else if (theVariant === 'single' && valueToConvert === null) {
        return [] as ItemType[];
      } else {
        return valueToConvert as ItemType[];
      }
    },
    []
  );

  // setup Downshift
  const {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
    setSelectedItems
  } = useMultipleSelection<ItemType>({
    getA11yRemovalMessage({ removedSelectedItem }) {
      return intlFmt('common.item.removed', itemToString(removedSelectedItem));
    },
    // this is necessary so that removeSelectedItem knows how to announce the change to the screen reader
    itemToString,
    selectedItems: getSelectedItemsForVariant(variant, value),
    onSelectedItemsChange: async ({ selectedItems }) => {
      // This cast is necessary because we are essentially downcasting.
      if (variant === 'single') {
        if (triggerBlurOnChange) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          //  if the form validates values with onBlur event,
          //  we need to ensure that it has correct values at the moment of validation.
          //  calling only onChange will not set the field to touch and
          //  as a result will not trigger form validation.
          await onChange((selectedItems?.[0] as T) ?? null);
          onBlur?.();
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onChange((selectedItems?.[0] as T) ?? null);
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange((selectedItems ?? []) as T[]);
      }
    }
  });

  // update the inputValue state
  useEffect(() => {
    if (variant === 'single') {
      setInputValue(value ? (value as ItemType)[itemLabelProp] : '');
    } else {
      setInputValue('');
    }
  }, [itemLabelProp, value, variant]);

  // get the filtered items depending on the state of the inputValue
  const filteredItems = useMemo(() => {
    if (variant === 'multi') {
      return items.filter(
        item =>
          !inputValue ||
          item[itemLabelProp].toLowerCase().includes(inputValue.toLowerCase())
      );
    } else {
      return items.filter(
        item =>
          !inputValue ||
          selectedItems.find(item => item[itemLabelProp] === inputValue) ||
          item[itemLabelProp].toLowerCase().includes(inputValue.toLowerCase())
      );
    }
  }, [itemLabelProp, items, selectedItems, inputValue, variant]);

  // handler for when the input value changes
  // this is a workaround to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
  // (instead of using useCombobox.stateChangeTypes.InputChange in the switch further down)
  // TODO: remove the workaround once Downshift fixed the issue on their side
  const onInputChange = useCallback(value => setInputValue(value), []);

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    toggleMenu,
    openMenu,
    getToggleButtonProps
  } = useCombobox({
    inputValue,
    items: filteredItems,
    itemToString,
    getA11ySelectionMessage({ selectedItem }) {
      if (selectedItem) {
        return intlFmt('common.item.selected', itemToString(selectedItem));
      }
      return '';
    },
    getA11yStatusMessage({ isOpen, resultCount, previousResultCount }) {
      if (isOpen) {
        if (!resultCount) {
          return intl('common.search.noentry');
        }
        if (resultCount !== previousResultCount) {
          return intlFmt('common.item.navigate', resultCount);
        }
      }
      return '';
    },
    scrollIntoView(item) {
      item?.scrollIntoView({ block: 'nearest' });
    },
    stateReducer: (_state, { changes, type }) => {
      if (variant === 'single') {
        return changes;
      }

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            // keep the menu open after selection
            isOpen: true,
            // with the previously selected option highlighted
            highlightedIndex: Math.max(0, items.indexOf(changes.selectedItem))
          };
        default:
          return changes;
      }
    },

    // "type" is missing in the types of onStateChange which is why this ignore is needed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onStateChange: ({ type, selectedItem }) => {
      // function to handle what should happen when the element loses focus
      const handleBlur = () => {
        if (variant === 'multi') {
          selectItem((null as unknown) as ItemType);
          setInputValue('');
        } else {
          setInputValue(selectedItems?.[0]?.[itemLabelProp] ?? '');
        }
      };

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            const multi = variant === 'multi';
            setInputValue(multi ? '' : selectedItem?.[itemLabelProp] ?? '');
            selectItem((null as unknown) as ItemType);
            if (selectedItems.includes(selectedItem)) {
              removeSelectedItem(selectedItem);
              break;
            }
            if (multi) {
              addSelectedItem(selectedItem);
            } else {
              setSelectedItems([selectedItem]);
            }
            // WCAG aria-live special selection announcement for downshift library
            setTimeout(() => {
              selectItem(selectedItem);
              setTimeout(() => selectItem((null as unknown) as ItemType), 201);
            }, 1);
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.InputBlur:
          onBlur?.();
          handleBlur();
          break;
        case useCombobox.stateChangeTypes.FunctionToggleMenu:
          if (isOpen) {
            handleBlur();
          }
          break;
        default:
          break;
      }
    }
  });

  return (
    <ToggleLayer
      isOpen={isOpen && !disabled && !readOnly}
      closeOnOutsideClick={true}
      placement={DEFAULT_COMBOBOX_LAYER_PLACEMENT}
      renderLayer={({ isOpen, layerProps, triggerRect }): JSX.Element => (
        <ComboboxDropdownMenu<ItemType>
          loading={false} // we don't have a loading state
          selectableItems={filteredItems}
          selectedItems={selectedItems}
          highlightedIndex={highlightedIndex}
          itemToKey={(item: ItemType) => item[itemValueProp]}
          renderItem={renderItem}
          layerProps={layerProps}
          triggerRect={triggerRect}
          isOpen={isOpen}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          wasLoaded={true}
          className={menuClassName}
          hideClearIcon={hideClearIcon}
        />
      )}
      {...menuOptions}
    >
      {({ triggerRef, isOpen }): JSX.Element => (
        <ComboboxInputElement<ItemType>
          {...props}
          triggerRef={triggerRef}
          variant={variant}
          selectedItems={selectedItems}
          isOpen={isOpen}
          getDropdownProps={getDropdownProps}
          removeSelectedItem={removeSelectedItem}
          placeholder={props.placeholder}
          getInputProps={getInputProps}
          toggleMenu={toggleMenu}
          openMenu={openMenu}
          getComboboxProps={getComboboxProps}
          disabled={disabled}
          readOnly={readOnly}
          getLabelProps={getLabelProps}
          itemToString={itemToString}
          showSearchIcon={false}
          showExpandTool={true}
          elementClickBehavior="toggleMenu"
          getToggleButtonProps={getToggleButtonProps}
          className={className}
          onInputChange={onInputChange}
          hideClearIcon={hideClearIcon}
          condensed={condensed}
          autoFocus={autoFocus}
        />
      )}
    </ToggleLayer>
  );
}
